import { useContext } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { config } from "../Environment";
import useRetryFetch from "./useRetryFetch";
import useAuth from "./useAuth";
import endpoints from "../lib/endpoints";
import { StartProcessInput, ResumeProcessInput } from "../lib/types";
import LoggerContext from "../context/LoggerProvider";

const useProcess = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const retryFetch = useRetryFetch();
  const { auth } = useAuth();
  const { Logger } = useContext(LoggerContext);

  /*
        TODO: replace all navigations to start activities with these functions


        ----

              sessionStorage.setItem("prescriptionID", prescriptionId);
      sessionStorage.setItem("processID", result.processId);
      sessionStorage.removeItem("nextActivity");

      const query = `wid=${prescriptionId}&pid=${result.processId}`;
      console.log(`navigating to run activity from location pathname ${location.pathname} and query ${location.search}`);
      navigate(`/activities/run?${query}`, { state: { from: location, forward: location } });

-----


        sessionStorage.setItem("prescriptionID", welcome._id);
        sessionStorage.setItem(
          "prescriptionHistoryID",
          result.prescriptionHistoryID
        );
        sessionStorage.setItem("runProcessID", result.processId);
        sessionStorage.removeItem("nextActivity");
        sessionStorage.setItem("isWelcome", "true");
        navigate("/preview");

-----

      const query = `wid=${prescriptionId}&pid=${result.processId}`;
      console.log(`startNewProcess: navigating to run activity`);
      navigate(`/activities/run?${query}`, { state: { background: location } });

      ----

            const query = `wid=${prescriptionId}&pid=${result.processId}`;
      console.log(`startNewProcess: navigating to run activity`);
      navigate(`/preview`, { state: { background: location } });

      ----
      sessionStorage.setItem("runProcessID")

      -----


        */

  const startProcess = async (input: StartProcessInput) => {
    try {
      // create a new process and start it
      const body: any = {
        email: auth.email,
      };

      // Add engagement name if provided
      if (input.engagementName) {
        body.engagementName = input.engagementName;
      }

      // Add tagging if provided
      if (input.tags && input.tags.length > 0) {
        body.tags = input.tags;
      }
      if (input.clientIdentifier) {
        body.clientIdentifier = input.clientIdentifier;
      }

      // Add trial mode parameters if provided
      if (input.trialMode !== undefined) {
        body.trialMode = input.trialMode;
      }
      if (input.trialProductId) {
        body.trialProductId = input.trialProductId;
      }
      if (input.spaceId) {
        body.spaceId = input.spaceId;
      }
      if (input.pwfId) {
        body.pwfId = input.pwfId;
      }

      let newResponse = await retryFetch(
        config.API_BASE + `/api/processes/${input.prescriptionId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify(body),
        }
      );

      if (!newResponse.ok) {
        throw new Error("start process API response error");
      }
      let result = await newResponse.json();
      Logger.debug(result);
      const query = `wid=${input.prescriptionId}&pid=${result.processId}`;
      console.log(`startNewProcess: navigating to run activity`);
      // isPreview && isMobile ? navigate("/preview") : window.open("/preview", "_blank");

      sessionStorage.setItem("prescriptionID", input.prescriptionId);
      sessionStorage.setItem("processID", result.processId);
      sessionStorage.removeItem("nextActivity");

      // Celebrate first engagement
      window.dispatchEvent(
        new CustomEvent("clary:celebration", {
          detail: { key: "first_engagement" },
        })
      );

      setTimeout(() => {
        navigate(`/activities/run?${query}`, {
          state: { from: input.from, forward: input.forward },
        });
      }, 100);
    } catch (err) {
      Logger.debug("failed to start process", err);
      if (typeof input.onError === "function") {
        input.onError();
      } else {
        // failed to start a new process, so return user to "from" location (possibly could be renamed "cancel" location)
        const from = input.from ?? { pathname: "/portal" };
        setTimeout(() => {
          navigate(`${from.pathname}${from.search ?? ""}`);
        }, 100);
      }
    }
  };

  const resumeProcess = async (input: ResumeProcessInput) => {
    try {
      let processToContinue: string = input.processId;
      let processResult;
      const nextActivityResponse = await retryFetch(
        config.API_BASE + `/api/processes/getnextactivity/${input.processId}`
      );
      if (!nextActivityResponse.ok) {
        throw new Error("get next activity API response error");
      }
      processResult = await nextActivityResponse.json();
      if (processResult.pendingInnerProcess) {
        Logger.debug(
          "resumeProcess PENDING INNER PROCESS detected, switching to this one!"
        );
        processToContinue = processResult.pendingInnerProcess;
        const innerNextActivityResponse = await retryFetch(
          config.API_BASE + `/api/processes/getnextactivity/${input.processId}`
        );
        if (!innerNextActivityResponse.ok) {
          throw new Error("get inner next activity API response error");
        }
        processResult = await innerNextActivityResponse.json();
      }

      console.log(`resumeProcess result ${JSON.stringify(processResult)}`);
      Logger.debug(processResult);

      // Note: We no longer set "nextActivity" in sessionStorage because
      // ActivityRunningPreview now reads it directly from the process object
      sessionStorage.setItem("prescriptionID", processResult.fiPrescription);
      sessionStorage.setItem("processID", processToContinue);

      const query = `wid=${processResult.fiPrescription}&pid=${processToContinue}`;
      setTimeout(() => {
        navigate(`/activities/run?${query}`, {
          state: { from: input.from, forward: input.forward },
        });
      }, 100);
    } catch (err) {
      if (typeof input.onError === "function") {
        input.onError();
      } else {
        // failed to resume the process, so return user to "from" location (possibly could be renamed "cancel" location)
        const from = input.from ?? { pathname: "/portal" };
        setTimeout(() => {
          navigate(`${from.pathname}${from.search ?? ""}`);
        }, 100);
      }
    }
  };

  return { startProcess, resumeProcess };
};

export default useProcess;
